/**
 * 1 つの文書（利用規約・プライバシーポリシー）の、前回の同意・確認からの主な変更点
 *
 * 改定の告知（PolicyUpdateNotice）と同意画面（pages/Consent.tsx）で使う。
 * 利用者への影響を先に出し、知らせる改定（notice 付き）の変更点を開いて出す。
 * 知らせなかった改定（表記の修正など）は「ほかの改定」に畳む。全文・改定履歴・前回の版へのリンクを付ける。
 */

import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import {
  POLICY_LABEL,
  POLICY_PATH,
  POLICY_REVISIONS,
  formatPolicyDate,
  type PolicyRevision,
} from '@/constants/policyRevisions';
import type { PolicyDocStatus } from '@/utils/policyStatus';

function RevisionChanges({ revision }: { revision: PolicyRevision }) {
  return (
    <div>
      <p className="text-xs font-medium text-text-600">{formatPolicyDate(revision.date)}</p>
      <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-text-800">
        {revision.changes.map((change) => (
          <li key={change}>{change}</li>
        ))}
      </ul>
    </div>
  );
}

export default function PolicyChangeSummary({ status }: { status: PolicyDocStatus }) {
  const label = POLICY_LABEL[status.doc];
  const path = POLICY_PATH[status.doc];
  const noticed = status.revisions.filter((r) => r.notice);
  // 知らせる改定が無い（内容の分からない版など）ときは、いちばん新しい改定を開いて出す
  const main = noticed.length > 0 ? noticed : status.revisions.slice(0, 1);
  const others = status.revisions.filter((r) => !main.includes(r));
  const impacts = status.revisions.map((r) => r.impact).filter((text): text is string => !!text);
  const previous = status.previousVersion;
  const previousArchived =
    !!previous && POLICY_REVISIONS[status.doc].slice(1).some((r) => r.date === previous);

  return (
    <section className="space-y-3">
      <h3 className="text-base font-semibold text-text-900">
        {label}（{formatPolicyDate(status.currentVersion)}施行）
      </h3>
      {impacts.map((impact) => (
        <p key={impact} className="rounded-lg bg-primary-50 px-3 py-2 text-sm text-primary-900">
          {impact}
        </p>
      ))}
      {main.length > 0 ? (
        <div className="space-y-3">
          <p className="text-xs text-text-500">主な変更点</p>
          {main.map((revision) => (
            <RevisionChanges key={revision.date} revision={revision} />
          ))}
        </div>
      ) : (
        <p className="text-sm text-text-700">変更の内容は全文をご確認ください。</p>
      )}
      {others.length > 0 && (
        <Disclosure>
          <DisclosureButton className="group flex items-center gap-1 text-left text-sm text-text-600 hover:text-text-800">
            {previous ? `前回の同意（${formatPolicyDate(previous)}の版）からの` : ''}ほかの改定（{others.length}件）
            <ChevronDownIcon className="h-4 w-4 transition-transform group-data-[open]:rotate-180" />
          </DisclosureButton>
          <DisclosurePanel className="space-y-3 rounded-lg bg-text-50 px-3 py-2">
            {others.map((revision) => (
              <RevisionChanges key={revision.date} revision={revision} />
            ))}
          </DisclosurePanel>
        </Disclosure>
      )}
      <p className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
        <Link to={path} target="_blank" rel="noreferrer" className="text-primary-700 underline">
          {label}の全文
        </Link>
        <Link to={`${path}#revisions`} target="_blank" rel="noreferrer" className="text-primary-700 underline">
          改定履歴
        </Link>
        {previousArchived && (
          <Link
            to={`${path}/versions/${previous}`}
            target="_blank"
            rel="noreferrer"
            className="text-primary-700 underline"
          >
            前回同意した版
          </Link>
        )}
      </p>
    </section>
  );
}
